// src/dashboard/layouts/ScenarioLayout.tsx
import { renderWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetId } from "@/shared/dashboard/widgets/types";
import type { MainColumnEntry } from "@/verticals/types";

interface Props {
  hero: { widget: WidgetId };
  mainColumn: Array<MainColumnEntry>;
  sideRail: Array<{ widget: WidgetId }>;
}

/**
 * Scenario-first layout. Hero widget at top, mainColumn widgets stacked
 * below the hero, sideRail (optional) on desktop. A mainColumn entry can
 * declare `pairWith` to render the entry as a 2-column row on md+.
 */
export function ScenarioLayout({ hero, mainColumn, sideRail }: Props) {
  const isFauProgram = hero.widget === "assignedCourseHero";

  return (
    <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
      <main
        className={`flex-1 overflow-y-auto ${isFauProgram ? "fau-program-page" : ""}`}
      >
        <div
          className={
            mainColumn.length > 0
              ? "w-full space-y-6 pb-6 sm:space-y-8 sm:pb-8"
              : "h-full w-full"
          }
        >
          {renderWidget(hero.widget, { slot: "hero" })}
          {mainColumn.length > 0 && (
            <div
              className={`mx-auto w-full ${isFauProgram ? "max-w-[1440px]" : "max-w-[1200px]"} space-y-6 px-4 sm:space-y-8 sm:px-8`}
            >
              {mainColumn.map((entry, i) => (
                <div key={`${entry.widget}-${i}`}>
                  {entry.pairWith ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        {renderWidget(entry.widget, { slot: "mainColumn" })}
                      </div>
                      <div>
                        {renderWidget(entry.pairWith, { slot: "mainColumn" })}
                      </div>
                    </div>
                  ) : (
                    renderWidget(entry.widget, { slot: "mainColumn" })
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {sideRail.length > 0 && (
        <aside className="hidden md:flex w-80 shrink-0 flex-col gap-4 border-l border-stone-200 bg-white/60 p-4">
          {sideRail.map(({ widget }, i) => (
            <div key={`${widget}-${i}`}>
              {renderWidget(widget, { slot: "sideRail" })}
            </div>
          ))}
        </aside>
      )}
    </div>
  );
}
